import * as Accordion from '@radix-ui/react-accordion'
import { Filters } from '@haus-storefront-react/filters'
import { type EnabledFilter, type FacetValueFilterType } from '@haus-storefront-react/shared-types'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './FacetFiltersAccordion.module.scss'
import classNames from 'classnames'

const DEFAULT_VISIBLE_OPTIONS = 6

interface FacetGroupValue {
  id: string
  name: string
  checked?: boolean
}

interface FacetGroupData {
  facetCode: string
  label: string
  values: FacetGroupValue[]
}

interface FacetFiltersAccordionProps {
  facetFilter: EnabledFilter<FacetValueFilterType>
  toggleFacetValue: (facetCode: string, valueId: string) => void
  defaultOpen?: boolean
  onFilterAnnounce?: (message: string) => void
  onResolved?: () => void
  index: number
  facet?: FacetGroupData
}

export function FacetFiltersAccordion({
  facetFilter,
  toggleFacetValue,
  defaultOpen = false,
  onFilterAnnounce,
  onResolved,
  index,
  facet: providedFacet,
}: FacetFiltersAccordionProps) {
  const { t } = useTranslation()
  const [showAllOptions, setShowAllOptions] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)

  const resolvedRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        onResolved?.()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const renderAccordion = (facet: FacetGroupData) => {
    if (facet.values.length === 0) {
      onResolved?.()
      return null
    }

    const visibleValues = showAllOptions
      ? facet.values
      : facet.values.slice(0, DEFAULT_VISIBLE_OPTIONS)
    const hasMoreOptions = facet.values.length > DEFAULT_VISIBLE_OPTIONS
    const isExpanded = showAllOptions

    return (
      <div ref={resolvedRef}>
        <Accordion.Root
          type="single"
          collapsible
          defaultValue={defaultOpen ? facet.facetCode : undefined}
          onValueChange={() => {
            if (!hasInteracted) setHasInteracted(true)
          }}
          className={classNames(
            styles.accordionRoot,
            index === 0 && styles.firstAccordionRoot,
            !hasInteracted && styles.skipInitialAnimation,
          )}
        >
          <Accordion.Item value={facet.facetCode} className={styles.accordionItem}>
            <Accordion.Header className={styles.accordionHeader}>
              <Accordion.Trigger className={styles.accordionTrigger}>
                <span>{facet.label}</span>
                <ChevronDownIcon className={styles.accordionChevron} aria-hidden />
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className={styles.accordionContent}>
              <div className={styles.filtersGroupContent}>
                {visibleValues.map((facetValue) => {
                  const inputId = `filter-${facet.facetCode}-${facetValue.id}`
                  return (
                    <label key={facetValue.id} htmlFor={inputId} className={styles.checkboxItem}>
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={!!facetValue.checked}
                        onChange={() => {
                          const isAdding = !facetValue.checked
                          toggleFacetValue(facet.facetCode, facetValue.id)
                          if (onFilterAnnounce) {
                            onFilterAnnounce(
                              isAdding
                                ? t('a11y.facetValueFilter.ariaLive.filterAdded', {
                                    filterValue: facetValue.name,
                                  })
                                : t('a11y.facetValueFilter.ariaLive.filterRemoved', {
                                    filterValue: facetValue.name,
                                  }),
                            )
                          }
                        }}
                        className={styles.srOnly}
                        aria-label={facetValue.name}
                      />
                      <span
                        className={styles.checkboxRoot}
                        data-state={facetValue.checked ? 'checked' : undefined}
                        aria-hidden
                      >
                        {facetValue.checked && (
                          <CheckIcon width={20} height={20} className={styles.checkboxIndicator} />
                        )}
                      </span>
                      <span>{facetValue.name}</span>
                    </label>
                  )
                })}
                {hasMoreOptions && (
                  <button
                    type="button"
                    onClick={() => setShowAllOptions((prev) => !prev)}
                    className={styles.showMoreButton}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? (
                      <>
                        {t('filters.showLess')}{' '}
                        <ChevronUpIcon className={styles.showMoreIcon} aria-hidden />
                      </>
                    ) : (
                      <>
                        {t('filters.showMore')}{' '}
                        <ChevronDownIcon className={styles.showMoreIcon} aria-hidden />
                      </>
                    )}
                  </button>
                )}
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      </div>
    )
  }

  if (providedFacet) {
    return renderAccordion(providedFacet)
  }

  return (
    <Filters.Group facetCode={facetFilter.facetCode}>
      {(facet) => renderAccordion(facet)}
    </Filters.Group>
  )
}

export default FacetFiltersAccordion
